import {
  BarChart3,
  BookText,
  Flag,
  HelpCircle,
  LogOut,
  Moon,
  Settings,
  Sun,
} from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { SUPER_ADMIN_PERMISSION } from '@/components/auth/SuperAdminRoute'
import { AnimatedThemeToggler } from '@/components/ui/animated-theme-toggler'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from '@/components/ui/sidebar'
import { useSettings } from '@/contexts/SettingsContext'
import { useAuthStore } from '@/stores/auth'
import { getAvatarColor, getInitials } from '@/utils/avatar'
import { useTheme } from './ThemeProvider'

const DOCS_URL = import.meta.env.VITE_DOCS_URL ?? 'https://docs.vwam.ai'

export function SidebarUserMenu() {
  const navigate = useNavigate()
  const { actualTheme } = useTheme()
  const { openSettings } = useSettings()
  const user = useAuthStore((state) => state.user)
  const role = useAuthStore((state) => state.role)
  const logout = useAuthStore((state) => state.logout)
  const hasPermission = useAuthStore((state) => state.hasPermission)

  if (!user) return null

  const fullName =
    user.fullName ||
    `${user.firstName || ''} ${user.lastName || ''}`.trim() ||
    'User'

  const handleLogout = async () => {
    await logout()
    navigate('/login')
  }

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <SidebarMenuButton size="lg" tooltip={fullName}>
              <div
                className={`flex size-8 shrink-0 items-center justify-center rounded-lg bg-gradient-to-r text-sm font-medium text-white ${getAvatarColor(
                  user.email
                )}`}
              >
                {user.avatar ? (
                  <img
                    src={user.avatar}
                    alt={fullName}
                    className="size-full rounded-lg object-cover"
                  />
                ) : (
                  getInitials(user.firstName, user.lastName)
                )}
              </div>

              <div className="grid flex-1 text-left text-sm leading-tight group-data-[collapsible=icon]:hidden">
                <span className="truncate font-semibold">{fullName}</span>
                <span className="truncate text-xs text-muted-foreground">
                  {user.email}
                </span>
              </div>
            </SidebarMenuButton>
          </DropdownMenuTrigger>

          <DropdownMenuContent side="top" align="start" className="w-64">
            <div className="px-2 py-1.5">
              <p className="truncate text-sm font-semibold">{fullName}</p>
              <p className="truncate text-xs text-muted-foreground">
                {user.email}
              </p>
            </div>

            <DropdownMenuSeparator />

            <DropdownMenuItem
              onClick={() =>
                window.open(DOCS_URL, '_blank', 'noopener,noreferrer')
              }
              className="cursor-pointer"
            >
              <HelpCircle className="mr-2 size-4" />
              Support
            </DropdownMenuItem>

            <DropdownMenuItem onClick={openSettings} className="cursor-pointer">
              <Settings className="mr-2 size-4" />
              Settings
            </DropdownMenuItem>

            {hasPermission(SUPER_ADMIN_PERMISSION) && (
              <DropdownMenuItem
                onClick={() => navigate('/platform-insights')}
                className="cursor-pointer"
              >
                <BarChart3 className="mr-2 size-4" />
                Platform Insights
              </DropdownMenuItem>
            )}

            {role?.name === 'Super Admin' && (
              <DropdownMenuItem
                onClick={() => navigate('/feature-flags')}
                className="cursor-pointer"
              >
                <Flag className="mr-2 size-4" />
                Feature Flags
              </DropdownMenuItem>
            )}

            {import.meta.env.DEV && user.email?.endsWith('@blueconic.com') && (
              <DropdownMenuItem
                onClick={() => navigate('/internal-docs')}
                className="cursor-pointer"
              >
                <BookText className="mr-2 size-4" />
                Internal Docs
              </DropdownMenuItem>
            )}

            <DropdownMenuSeparator />

            <div className="flex items-center justify-between px-2 py-1.5">
              <div className="flex items-center gap-2">
                {actualTheme === 'dark' ? (
                  <Moon className="size-4" />
                ) : (
                  <Sun className="size-4" />
                )}
                <span className="text-sm">Dark Mode</span>
              </div>
              <AnimatedThemeToggler className="ml-auto" />
            </div>

            <DropdownMenuSeparator />

            <DropdownMenuItem onClick={handleLogout} className="cursor-pointer">
              <LogOut className="mr-2 size-4" />
              Log out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    </SidebarMenu>
  )
}
